import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import ApiClientAsync from "../api/ApiClientAsync";
import adaptStrategyBenchmarkChartData from "../lib/StrategyBenchmarkChart/adaptStrategyBenchmarkChartData";
import {
  getStrategyBenchmarkChartTimeframes,
  STRATEGY_BENCHMARK_CHART_TIMEFRAME
} from "../lib/StrategyBenchmarkChart/strategyBenchmarkChartTimeframes";
import transformStrategyBenchmarkChartSeries from "../lib/StrategyBenchmarkChart/transformStrategyBenchmarkChartSeries";
import {
  buildStrategyBenchmarkChartMarketQuery,
  resolveStrategyDashboardChartMarket
} from "../lib/StrategyDashboard/strategyDashboardChartMarkets";
import { getAuthToken } from "../redux/modules/auth/login/selector";
import { getLocale } from "../redux/modules/translations/selector";

const DEFAULT_TIMEFRAME = STRATEGY_BENCHMARK_CHART_TIMEFRAME.YTD;
const EMPTY_RAW_DATA = {
  strategy: {},
  comparisons: []
};

const EMPTY_ADAPTED = adaptStrategyBenchmarkChartData(EMPTY_RAW_DATA);

export default function useStrategyBenchmarkChart({
  strategy,
  markets: marketsProp,
  selectedMarket: selectedMarketProp
}) {
  const locale = useSelector(getLocale);
  const authToken = useSelector(getAuthToken);
  const [timeframe, setTimeframeState] = useState(DEFAULT_TIMEFRAME);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [comparisonType, setComparisonType] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSelectedMarket, setUserSelectedMarket] = useState(null);
  const fetchGenerationRef = useRef(0);

  const marketsKey = Array.isArray(marketsProp)
    ? marketsProp.map(market => String(market).toUpperCase()).join(",")
    : "";
  const markets = useMemo(() => (marketsKey ? marketsKey.split(",") : []), [
    marketsKey
  ]);
  const isMultiMarket = markets.length > 0;

  const selectedMarket = useMemo(() => {
    if (!isMultiMarket) {
      return null;
    }

    return resolveStrategyDashboardChartMarket(
      markets,
      userSelectedMarket ?? selectedMarketProp
    );
  }, [isMultiMarket, markets, selectedMarketProp, userSelectedMarket]);

  const allowedTimeframes = useMemo(
    () => getStrategyBenchmarkChartTimeframes(strategy),
    [strategy]
  );

  const timeframeOptions = useMemo(
    () =>
      allowedTimeframes.map(value => ({
        value,
        label: value
      })),
    [allowedTimeframes]
  );

  const setTimeframe = useCallback(
    nextTimeframe => {
      if (allowedTimeframes.includes(nextTimeframe)) {
        setTimeframeState(nextTimeframe);
      }
    },
    [allowedTimeframes]
  );

  useEffect(() => {
    if (!allowedTimeframes.includes(timeframe)) {
      setTimeframeState(DEFAULT_TIMEFRAME);
    }
  }, [allowedTimeframes, timeframe]);

  useEffect(() => {
    if (!strategy) {
      setEntries([]);
      setLoading(false);
      return undefined;
    }

    const generation = ++fetchGenerationRef.current;
    let cancelled = false;
    const slots = isMultiMarket ? markets : [null];

    const load = async () => {
      setLoading(true);

      const apiClientAsync = new ApiClientAsync(locale, authToken);

      const results = await Promise.all(
        slots.map(async market => {
          try {
            const raw = await apiClientAsync.get({
              urlPath: "app.strategies.returnRateChart",
              variables: {
                strategyType: strategy,
                timeframe,
                marketQuery: buildStrategyBenchmarkChartMarketQuery(market)
              },
              skipAccessCheck: true
            });

            return {
              market,
              adapted: adaptStrategyBenchmarkChartData(raw || EMPTY_RAW_DATA),
              error: null
            };
          } catch (fetchError) {
            return {
              market,
              adapted: EMPTY_ADAPTED,
              error: fetchError
            };
          }
        })
      );

      if (cancelled || generation !== fetchGenerationRef.current) {
        return;
      }

      setEntries(results);
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [authToken, isMultiMarket, locale, markets, strategy, timeframe]);

  const selectedEntry = useMemo(() => {
    if (!isMultiMarket) {
      return entries[0] || null;
    }

    return (
      entries.find(entry => entry.market === selectedMarket) ||
      entries[0] ||
      null
    );
  }, [entries, isMultiMarket, selectedMarket]);

  const adaptedBenchmarkData = selectedEntry?.adapted || EMPTY_ADAPTED;
  const hasSelectedData = Boolean(selectedEntry?.adapted);
  const isInitialLoading = loading && !hasSelectedData;
  const isRefetching = loading && hasSelectedData;

  const comparisonOptions = useMemo(
    () =>
      adaptedBenchmarkData.comparisons.map(item => ({
        value: item.key,
        label: item.label
      })),
    [adaptedBenchmarkData]
  );

  const activeComparisonType = useMemo(() => {
    const hasSelectedComparison = comparisonOptions.some(
      option => option.value === comparisonType
    );

    if (hasSelectedComparison) {
      return comparisonType;
    }

    return comparisonOptions[0]?.value || "comparison";
  }, [comparisonOptions, comparisonType]);

  const rates = useMemo(
    () =>
      adaptedBenchmarkData.comparisons.reduce(
        (accumulator, item) => ({
          ...accumulator,
          [item.key]: item.rateOfReturn
        }),
        {
          strategy: adaptedBenchmarkData.strategy.rateOfReturn
        }
      ),
    [adaptedBenchmarkData]
  );

  const {
    chartSeries,
    strategyRateOfReturn,
    comparisonRateOfReturn
  } = useMemo(
    () =>
      transformStrategyBenchmarkChartSeries(
        adaptedBenchmarkData,
        activeComparisonType
      ),
    [adaptedBenchmarkData, activeComparisonType]
  );

  const marketOptions = useMemo(() => {
    if (!isMultiMarket) {
      return [];
    }

    return markets.map(market => {
      const entry = entries.find(item => item.market === market);
      return {
        value: market,
        label: market,
        rateOfReturn: entry?.adapted?.strategy?.rateOfReturn ?? null,
        isLoading: loading && !entry
      };
    });
  }, [entries, isMultiMarket, loading, markets]);

  const setSelectedMarket = useCallback(
    market => {
      if (!isMultiMarket) {
        return;
      }

      const resolved = resolveStrategyDashboardChartMarket(markets, market);
      if (resolved) {
        setUserSelectedMarket(resolved);
      }
    },
    [isMultiMarket, markets]
  );

  const openFullscreen = useCallback(() => {
    setIsFullscreenOpen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreenOpen(false);
  }, []);

  return {
    selectedMarket,
    setSelectedMarket,
    marketOptions,
    comparisonType: activeComparisonType,
    setComparisonType,
    comparisonOptions,
    timeframe,
    setTimeframe,
    isFullscreenOpen,
    openFullscreen,
    closeFullscreen,
    chartSeries,
    strategyRateOfReturn,
    comparisonRateOfReturn,
    rates,
    loading,
    isInitialLoading,
    isRefetching,
    error: selectedEntry?.error ?? null,
    timeframeOptions
  };
}
